import type {
  PagedFlipPageSlot,
  PagedFlipStandalonePageSlot,
  Template,
  TemplateNode,
} from '@/app/dashboard/templates/components/types/template';

export const PAGED_FLIP_STANDALONE_SLOTS = ['cover', 'toc', 'chapter', 'back-cover'] as const;
export const PAGED_FLIP_CONTENT_SLOTS = [
  'section-divider',
  'category-spotlight',
  'promotion-showcase',
  'featured-showcase',
  'new-arrivals',
  'content',
] as const;
export const PAGED_FLIP_PAGE_SLOTS = [
  ...PAGED_FLIP_STANDALONE_SLOTS,
  ...PAGED_FLIP_CONTENT_SLOTS,
] as const satisfies readonly PagedFlipPageSlot[];

const STANDALONE_SLOT_FIELDS: Record<PagedFlipStandalonePageSlot, keyof Template> = {
  cover: 'coverNodes',
  toc: 'tocNodes',
  chapter: 'chapterNodes',
  'back-cover': 'backCoverNodes',
};

function cloneNodes(nodes?: TemplateNode[] | null) {
  if (!Array.isArray(nodes) || nodes.length === 0) return [] as TemplateNode[];
  return JSON.parse(JSON.stringify(nodes)) as TemplateNode[];
}

function isLegacyPagedNodeMap(
  pagedNodes: Template['pagedNodes'],
): pagedNodes is Partial<Record<PagedFlipPageSlot, TemplateNode[]>> {
  return Boolean(pagedNodes) && typeof pagedNodes === 'object' && !Array.isArray(pagedNodes);
}

function matchesPageSlot(
  pageSlot: PagedFlipPageSlot | PagedFlipPageSlot[] | undefined,
  slot: PagedFlipPageSlot,
) {
  if (Array.isArray(pageSlot)) {
    return pageSlot.includes(slot);
  }

  if (typeof pageSlot === 'string') {
    return pageSlot === slot;
  }

  return slot === 'content';
}

export function getStandaloneTemplatePageNodes(
  template: Template | null | undefined,
  slot: PagedFlipStandalonePageSlot,
) {
  const field = STANDALONE_SLOT_FIELDS[slot];
  const directNodes = cloneNodes(template?.[field] as TemplateNode[] | undefined);
  if (directNodes.length > 0) return directNodes;

  if (Array.isArray(template?.pagedNodes)) {
    const matchedNodes = template.pagedNodes.filter((node) => matchesPageSlot(node.props?.pageSlot, slot));
    if (matchedNodes.length > 0) return cloneNodes(matchedNodes);
  }

  if (isLegacyPagedNodeMap(template?.pagedNodes)) {
    return cloneNodes(template?.pagedNodes?.[slot]);
  }

  return [] as TemplateNode[];
}

export function getTemplatePagedNodes(
  template: Template | null | undefined,
  slot: PagedFlipPageSlot,
) {
  if (slot === 'cover' || slot === 'toc' || slot === 'chapter' || slot === 'back-cover') {
    return getStandaloneTemplatePageNodes(template, slot);
  }

  if (Array.isArray(template?.pagedNodes)) {
    const matchedNodes = template.pagedNodes.filter((node) => matchesPageSlot(node.props?.pageSlot, slot));
    if (matchedNodes.length > 0) return cloneNodes(matchedNodes);
  }

  if (isLegacyPagedNodeMap(template?.pagedNodes)) {
    return cloneNodes(template?.pagedNodes?.[slot]);
  }

  return [] as TemplateNode[];
}

export function getAllTemplateNodeRoots(template?: Template | null) {
  const roots: TemplateNode[] = [
    ...(template?.nodes || []),
    ...(template?.coverNodes || []),
    ...(template?.tocNodes || []),
    ...(template?.chapterNodes || []),
    ...(template?.backCoverNodes || []),
  ];

  if (Array.isArray(template?.pagedNodes)) {
    roots.push(...template.pagedNodes);
    return roots;
  }

  if (isLegacyPagedNodeMap(template?.pagedNodes)) {
    Object.values(template.pagedNodes).forEach((slotNodes) => {
      if (Array.isArray(slotNodes)) {
        roots.push(...slotNodes);
      }
    });
  }

  return roots;
}
